import type {
  ImprintLogoTarget,
  UploadImprintLogoInput,
  AssignableUser,
  MyTask,
  SetProductionLeadInput,
  SetProductionStageAssignmentInput,
  SaveProductionScheduleInput,
  SetUserTeamInput,
  UpdateUserInput,
  BlockItemInput,
  CatalogFilters,
  CatalogCollectionInput,
  CatalogCollectionSummary,
  CatalogItem,
  CatalogItemActivity,
  CatalogItemDocument,
  CatalogItemMockupAsset,
  CatalogItemEditionSnapshot,
  CatalogItemInput,
  CatalogItemType,
  CatalogPage,
  CatalogPagination,
  CatalogProduct,
  CatalogProductSummary,
  CatalogSearchResult,
  CreateKitTemplateInput,
  CreatePipelineTemplateInput,
  CreateCatalogProductInput,
  CreateProductDriveFoldersInput,
  DashboardData,
  EditorialFunction,
  DeleteCatalogItemDocumentInput,
  DeleteCatalogItemDocumentResult,
  DeleteCatalogItemMockupAssetInput,
  DeleteCatalogItemMockupAssetResult,
  DeleteCatalogProductImageInput,
  DeleteCatalogProductImageResult,
  DeleteCatalogProductResult,
  DriveItemStageFolders,
  CatalogRecordExtraction,
  UploadSupplementaryFileInput,
  SupplementaryFileKind,
  CreditsExtraction,
  FaoExtraction,
  ProductDriveFoldersResult,
  DriveSyncResult,
  DriveUploadedFile,
  GoogleDriveConnection,
  ImprintInput,
  ImprintSummary,
  InviteUserInput,
  ItemTypeInput,
  ItemTypeVariantInput,
  NotificationItem,
  PipelineCatalogEntry,
  SavePipelineStageEstimatesInput,
  ProductionFilters,
  ProductionRun,
  ProductionRunSummary,
  PipelineVersionContentInput,
  PrintShopInput,
  PrintShopSummary,
  TemplateCatalog,
  TransitionItemInput,
  SkipProductionItemInput,
  ReturnProductionItemInput,
  SaveCatalogProductInput,
  StartProductionInput,
  SyncProductionWithDriveInput,
  UpdateProductionInput,
  UpdateProductionQuantitiesInput,
  UploadCatalogItemDocumentInput,
  UploadCatalogItemMockupAssetInput,
  UploadCatalogProductImageInput,
  UploadToDriveStageInput,
  CatalogProductImage,
  CatalogProductImagePreview,
  ReplaceStudioKitImagesResult,
  StagedCatalogProductImage,
  CatalogPublicMediaSelection,
  CatalogImageAltText,
  SaveCatalogPublicMediaOptions,
  KitPresetCatalogEntry,
  WorkspaceUser,
  ProductionPrintDelivery,
  ProductionPrintItem,
  PipelineStageEstimateSet,
} from "@/types/domain"
import type { PrintChecklistKey } from "@/lib/print-checklist"

export interface EditorialRepository {
  searchCatalog(query: string, filters?: CatalogFilters, pagination?: CatalogPagination): Promise<CatalogPage<CatalogSearchResult>>
  listCatalogProducts(filters?: CatalogFilters, pagination?: CatalogPagination): Promise<CatalogPage<CatalogProductSummary>>
  listCatalogPublicationYears(): Promise<number[]>
  listCatalogCollections(): Promise<CatalogCollectionSummary[]>
  createCatalogCollection(input: CatalogCollectionInput): Promise<string>
  updateCatalogCollection(collectionId: string, input: CatalogCollectionInput, expectedVersion: number): Promise<void>
  setCatalogCollectionActive(collectionId: string, active: boolean, reason: string, expectedVersion: number): Promise<void>
  deleteCatalogCollection(collectionId: string): Promise<void>
  getCatalogProduct(productId: string): Promise<CatalogProduct>
  getCatalogItem(itemId: string): Promise<CatalogItem>
  listCatalogItemActivity(itemId: string, limit?: number, offset?: number): Promise<CatalogItemActivity[]>
  getCatalogItemEditionSnapshot(snapshotId: string): Promise<CatalogItemEditionSnapshot>
  createCatalogProduct(input: CreateCatalogProductInput): Promise<string>
  saveCatalogProduct(productId: string, input: SaveCatalogProductInput, expectedVersion: number, confirmActiveProduction?: boolean): Promise<CatalogProduct>
  saveCatalogItem(itemId: string, input: CatalogItemInput, expectedVersion: number, confirmActiveProduction?: boolean): Promise<CatalogItem>
  createCatalogItemEdition(itemId: string, expectedVersion: number, confirmActiveProduction?: boolean): Promise<CatalogItem>
  deleteCatalogProduct(productId: string, expectedVersion: number): Promise<DeleteCatalogProductResult>
  listProductionRuns(filters?: ProductionFilters): Promise<ProductionRunSummary[]>
  listProductionPrintItems(runIds: string[]): Promise<ProductionPrintItem[]>
  getProductionRun(runId: string): Promise<ProductionRun>
  startProduction(input: StartProductionInput): Promise<string>
  updateProduction(input: UpdateProductionInput): Promise<ProductionRun>
  updateProductionQuantities(input: UpdateProductionQuantitiesInput): Promise<ProductionRun>
  stopProduction(runId: string, expectedVersion: number, reason: string): Promise<ProductionRun>
  resumeProduction(runId: string, expectedVersion: number): Promise<ProductionRun>
  deleteProduction(runId: string, expectedVersion: number): Promise<void>
  cancelProduction(runId: string, expectedVersion: number, reason: string): Promise<ProductionRun>
  followProduction(runId: string, follow: boolean): Promise<void>
  transitionProductionItem(input: TransitionItemInput): Promise<ProductionRun>
  skipProductionItem(input: SkipProductionItemInput): Promise<ProductionRun>
  returnProductionItem(input: ReturnProductionItemInput): Promise<ProductionRun>
  blockProductionItem(input: BlockItemInput): Promise<ProductionRun>
  unblockProductionItem(input: Omit<BlockItemInput, "reason">): Promise<ProductionRun>
  toggleProductionChecklist(checklistId: string, expectedVersion: number): Promise<ProductionRun>
  toggleProductionGate(gateId: string, expectedVersion: number): Promise<ProductionRun>
  assignProductionItemPrintShop(itemId: string, printShopId: string | undefined, expectedVersion: number): Promise<ProductionRun>
  setProductionItemPrintDeliveries(itemId: string, deliveries: ProductionPrintDelivery[], expectedVersion: number): Promise<ProductionRun>
  setProductionItemQuoteUrl(itemId: string, quoteUrl: string | null, expectedVersion: number): Promise<ProductionRun>
  setProductionItemPrintChecklist(itemId: string, key: PrintChecklistKey, done: boolean, expectedVersion: number): Promise<ProductionRun>
  listAssignableUsers(): Promise<AssignableUser[]>
  setProductionStageAssignment(input: SetProductionStageAssignmentInput): Promise<ProductionRun>
  setProductionLead(input: SetProductionLeadInput): Promise<ProductionRun>
  saveProductionSchedule(input: SaveProductionScheduleInput): Promise<ProductionRun>
  listMyTasks(): Promise<MyTask[]>
  getItemTypeCatalog(): Promise<CatalogItemType[]>
  getKitPresets(): Promise<KitPresetCatalogEntry[]>
  getPipelineCatalog(): Promise<PipelineCatalogEntry[]>
  uploadCatalogItemDocument(input: UploadCatalogItemDocumentInput): Promise<CatalogItem>
  deleteCatalogItemDocument(input: DeleteCatalogItemDocumentInput): Promise<DeleteCatalogItemDocumentResult>
  getCatalogItemDocumentUrl(document: CatalogItemDocument): Promise<string>
  uploadCatalogItemMockupAsset(input: UploadCatalogItemMockupAssetInput): Promise<CatalogItem>
  deleteCatalogItemMockupAsset(input: DeleteCatalogItemMockupAssetInput): Promise<DeleteCatalogItemMockupAssetResult>
  getCatalogItemMockupAssetUrl(asset: CatalogItemMockupAsset): Promise<string>
  uploadCatalogProductImage(input: UploadCatalogProductImageInput): Promise<CatalogProduct>
  deleteCatalogProductImage(input: DeleteCatalogProductImageInput): Promise<DeleteCatalogProductImageResult>
  getCatalogProductImageUrl(image: CatalogProductImage): Promise<string>
  getCatalogProductImagePreview(image: CatalogProductImage): Promise<CatalogProductImagePreview>
  /** Kit do produto no formato da API do Mockup Studio (`/v2/kits/:id`), repassado sem leitura ao Studio. */
  getMockupStudioKit(productId: string): Promise<unknown>
  /** Envia as imagens ao Storage sem registrá-las; `replaceStudioKitImages` registra, `discardStagedCatalogProductImages` desfaz. */
  stageCatalogProductImages(productId: string, files: File[], onProgress?: (uploaded: number) => void): Promise<StagedCatalogProductImage[]>
  /** Registra o kit padrão gerado e apaga o anterior, menos as imagens que estão na galeria pública. */
  replaceStudioKitImages(productId: string, images: StagedCatalogProductImage[], expectedVersion: number): Promise<ReplaceStudioKitImagesResult>
  discardStagedCatalogProductImages(images: StagedCatalogProductImage[]): Promise<void>
  saveCatalogPublicMedia(productId: string, media: CatalogPublicMediaSelection[], expectedVersion: number, altTexts?: CatalogImageAltText[], options?: SaveCatalogPublicMediaOptions): Promise<CatalogProduct>
  publishCatalogProduct(productId: string, publicSlug: string, expectedVersion: number): Promise<CatalogProduct>
  unpublishCatalogProduct(productId: string, expectedVersion: number): Promise<CatalogProduct>
  getDashboard(): Promise<DashboardData>
  listNotifications(): Promise<NotificationItem[]>
  markNotificationRead(notificationId: string): Promise<void>
  getTemplateCatalog(): Promise<TemplateCatalog>
  createItemType(input: ItemTypeInput): Promise<string>
  updateItemType(itemTypeId: string, name: string): Promise<void>
  saveItemType(input: ItemTypeInput, itemTypeId?: string, expectedVersion?: number): Promise<string>
  deleteItemType(itemTypeId: string, expectedVersion: number): Promise<void>
  saveItemTypeVariant(itemTypeId: string, input: ItemTypeVariantInput, variantId?: string, expectedVersion?: number): Promise<string>
  deleteItemTypeVariant(variantId: string, expectedVersion: number): Promise<void>
  createKitTemplate(input: CreateKitTemplateInput): Promise<string>
  updateKitTemplate(templateId: string, input: CreateKitTemplateInput, expectedVersion?: number): Promise<string>
  deleteKitTemplate(templateId: string): Promise<void>
  createPipelineTemplate(input: CreatePipelineTemplateInput): Promise<string>
  updatePipelineTemplateVersion(versionId: string, input: PipelineVersionContentInput, expectedVersion?: number): Promise<void>
  createPipelineTemplateVersion(sourceVersionId: string): Promise<string>
  publishPipelineTemplateVersion(versionId: string): Promise<void>
  setPipelineTemplateActive(templateId: string, active: boolean): Promise<void>
  setPipelineStageFunction(stageId: string, editorialFunction: EditorialFunction | null): Promise<void>
  deletePipelineTemplate(templateId: string): Promise<void>
  listPipelineStageEstimates(pipelineTemplateId: string): Promise<PipelineStageEstimateSet>
  savePipelineStageEstimates(input: SavePipelineStageEstimatesInput): Promise<void>
  listImprints(): Promise<ImprintSummary[]>
  createImprint(input: ImprintInput): Promise<string>
  updateImprint(imprintId: string, input: ImprintInput, expectedVersion?: number): Promise<void>
  setImprintActive(imprintId: string, active: boolean, reason: string): Promise<void>
  deleteImprint(imprintId: string): Promise<void>
  uploadImprintLogo(input: UploadImprintLogoInput): Promise<void>
  deleteImprintLogo(input: ImprintLogoTarget): Promise<void>
  listUsers(): Promise<WorkspaceUser[]>
  inviteUser(input: InviteUserInput): Promise<string>
  setUserRole(userId: string, role: WorkspaceUser["role"]): Promise<void>
  setUserTeam(input: SetUserTeamInput): Promise<void>
  updateUser(input: UpdateUserInput): Promise<void>
  deleteUser(userId: string): Promise<void>
  listPrintShops(): Promise<PrintShopSummary[]>
  createPrintShop(input: PrintShopInput): Promise<string>
  updatePrintShop(printShopId: string, input: PrintShopInput, expectedVersion?: number): Promise<void>
  deletePrintShop(printShopId: string): Promise<void>
  getGoogleDriveConnection(): Promise<GoogleDriveConnection>
  startGoogleDriveConnection(returnUrl: string): Promise<string>
  disconnectGoogleDrive(): Promise<void>
  syncProductionWithDrive(input: SyncProductionWithDriveInput): Promise<DriveSyncResult>
  getDriveItemStageFolders(input: { runId: string; itemId: string }): Promise<DriveItemStageFolders>
  extractCatalogRecordFromDrive(fileUrl: string): Promise<CatalogRecordExtraction>
  /** Envia o .docx (ficha catalográfica, de créditos ou FAO) para a pasta "Arquivos complementares" do item no Drive. */
  uploadSupplementaryFile(input: UploadSupplementaryFileInput): Promise<DriveUploadedFile>
  /** Move para a lixeira do Drive o arquivo do tipo salvo no item, se estiver em "Arquivos complementares". */
  trashSupplementaryFile(itemId: string, kind: SupplementaryFileKind): Promise<{ trashed: boolean }>
  extractCreditsFromDrive(fileUrl: string): Promise<CreditsExtraction>
  extractFaoFromDrive(fileUrl: string): Promise<FaoExtraction>
  createProductDriveFolders(input: CreateProductDriveFoldersInput): Promise<ProductDriveFoldersResult>
  uploadFileToDriveStage(input: UploadToDriveStageInput): Promise<DriveUploadedFile>
}

export class DomainError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message)
  }
}
